/**
 * ScriptsTab.tsx — the Scripts tab (design doc §"Scripts tab — the core
 * interaction", ADR-084 D1 `Script`/`Step`, D4). Script selector → step
 * list (shared `ReorderableList`, drag + keyboard "Mover para cima/baixo",
 * per-step `enabled` toggle, select-to-edit) → `StepForm.tsx` settings form.
 *
 * Reorder is silent and unconstrained by design — D4 carries no ordering
 * rule (ADR-084 Consequences, Aron confirmed repeatedly). "+ Nova etapa"
 * seeds a placeholder `step_id` the operator must edit; uniqueness is
 * checked client-side across every script in the document (D4 rule 1),
 * still enforced server-side. New product scripts come from a CLOSED
 * product-code picker (Aron's ruling on the design doc's open question 3 —
 * no free-text product code, an operator typo would silently make the
 * script unreachable per D2's product-code resolution), fed by the same
 * `apiClient.getProductCatalogue()` call the Products tab uses.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import type { FactDef, ProductConfig, Script, SkillVersionFieldError, Step } from '../../api/types';
import { Button } from '../../components/Button';
import { inputClass } from '../../components/Field';
import { textActionClass } from './agenticFieldHelpers';
import { ReorderableList } from './ReorderableList';
import { StepForm } from './StepForm';
import floating from '../../lib/floating';

export interface ScriptsTabProps {
  apiClient: ApiClient;
  scripts: Script[];
  onChange: (next: Script[]) => void;
  facts: FactDef[];
  tools: string[];
  disabled?: boolean;
  fieldErrors: SkillVersionFieldError[];
}

const errorFor = (fieldErrors: SkillVersionFieldError[], path: string): string | undefined =>
  fieldErrors.find((error) => error.field_path === path)?.reason;

const blankStep = (stepId: string): Step => ({
  step_id: stepId,
  goal: '',
  enabled: true,
  required_facts: [],
  allowed_tools: [],
  exit_when: '',
  advance_when: null,
  on_decline: '',
  on_ineligible: '',
  on_consent_not_classified: '',
  retry_guidance: '',
});

let newStepSeq = 0;

export const ScriptsTab = ({
  apiClient,
  scripts,
  onChange,
  facts,
  tools,
  disabled = undefined,
  fieldErrors,
}: ScriptsTabProps) => {
  const { t } = useTranslation();
  const entryScript = scripts.find((script) => script.kind === 'entry') ?? null;
  const [selectedScriptKey, setSelectedScriptKey] = useState<string | null>(
    entryScript?.product_script_key ?? null,
  );
  const [openStepByScript, setOpenStepByScript] = useState<Record<string, string>>({});
  const [newScriptOpen, setNewScriptOpen] = useState(false);
  const [newProductCode, setNewProductCode] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [products, setProducts] = useState<ProductConfig[]>([]);

  useEffect(() => {
    let cancelled = false;
    floating(
      apiClient.getProductCatalogue().then(
        (result) => {
          if (!cancelled) setProducts(result);
        },
        () => {
          if (!cancelled) setProducts([]);
        },
      ),
    );
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  // Force-open the step (and script) named by a rule-1/3/4 issue (design doc
  // "Inline validation rendering" rows 1/3/4). Rule 1's issue names only the
  // duplicate occurrence — the other script carrying the same step_id is
  // found here so it opens too if the operator switches there.
  useEffect(() => {
    const withStep = fieldErrors.filter(
      (error) => error.script_key != null && error.step_id != null,
    );
    if (withStep.length === 0) return;
    setOpenStepByScript((prev) => {
      const next = { ...prev };
      withStep.forEach((error) => {
        next[error.script_key!] = error.step_id!;
        if (error.reason.includes('not unique across the document')) {
          const twin = scripts.find(
            (script) =>
              script.product_script_key !== error.script_key &&
              script.steps.some((step) => step.step_id === error.step_id),
          );
          if (twin) next[twin.product_script_key] = error.step_id!;
        }
      });
      return next;
    });
    setSelectedScriptKey((current) => current ?? withStep[0]!.script_key!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldErrors]);

  const selectedScript =
    scripts.find((script) => script.product_script_key === selectedScriptKey) ?? entryScript;
  const usedProductCodes = new Set(
    scripts.map((script) => script.product_code).filter((code): code is string => code != null),
  );
  const availableProducts = products.filter(
    (product) => !usedProductCodes.has(product.product_code),
  );

  const allStepIds = useMemo(() => {
    const counts = new Map<string, number>();
    scripts.forEach((script) => {
      script.steps.forEach((step) => {
        counts.set(step.step_id, (counts.get(step.step_id) ?? 0) + 1);
      });
    });
    return counts;
  }, [scripts]);

  const updateScript = (key: string, patch: Partial<Script>) =>
    onChange(
      scripts.map((script) =>
        script.product_script_key === key ? { ...script, ...patch } : script,
      ),
    );

  const updateSteps = (key: string, steps: Step[]) => updateScript(key, { steps });

  const openStep = (key: string, stepId: string | null) =>
    setOpenStepByScript((prev) =>
      stepId == null ? { ...prev, [key]: '' } : { ...prev, [key]: stepId },
    );

  const addStep = (key: string) => {
    let stepId: string;
    do {
      newStepSeq += 1;
      stepId = `nova_etapa_${newStepSeq}`;
    } while (allStepIds.has(stepId));
    const script = scripts.find((s) => s.product_script_key === key);
    if (script == null) return;
    updateSteps(key, [...script.steps, blankStep(stepId)]);
    openStep(key, stepId);
  };

  const removeStep = (key: string, stepId: string) => {
    const script = scripts.find((s) => s.product_script_key === key);
    if (script == null) return;
    updateSteps(
      key,
      script.steps.filter((step) => step.step_id !== stepId),
    );
    openStep(key, null);
  };

  const createProductScript = () => {
    if (newProductCode === '' || newDisplayName.trim() === '') return;
    const key = `${newProductCode}_script`;
    const script: Script = {
      product_script_key: key,
      kind: 'product',
      version: 1,
      product_code: newProductCode,
      display_name: newDisplayName.trim(),
      steps: [],
    };
    onChange([...scripts, script]);
    setSelectedScriptKey(key);
    setNewScriptOpen(false);
    setNewProductCode('');
    setNewDisplayName('');
  };

  const scriptsBanner = errorFor(fieldErrors, 'scripts');
  const productCodeConflicts = fieldErrors.filter((error) =>
    error.field_path.endsWith('.product_code'),
  );

  return (
    <section aria-labelledby="agentic-scripts-heading" className="flex flex-col gap-4">
      <h3 id="agentic-scripts-heading" className="text-h3 text-ink-1">
        {t('config.editAgentic.scripts.heading')}
      </h3>

      {scriptsBanner != null ? (
        <p
          role="alert"
          data-testid="agentic-scripts-entry-error"
          className="min-w-0 break-words [overflow-wrap:anywhere] text-meta text-status-down"
        >
          {scriptsBanner}
        </p>
      ) : null}
      {productCodeConflicts.map((error) => (
        <p
          key={error.field_path}
          role="alert"
          data-testid={`agentic-scripts-conflict-${error.script_key}`}
          className="min-w-0 break-words [overflow-wrap:anywhere] text-meta text-status-down"
        >
          {error.script_key}: {error.reason}
        </p>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <label
          htmlFor="agentic-scripts-selector"
          className="flex items-center gap-2 text-meta text-ink-2"
        >
          {t('config.editAgentic.scripts.selectorLabel')}
          <select
            id="agentic-scripts-selector"
            data-testid="agentic-scripts-selector"
            value={selectedScriptKey ?? ''}
            disabled={disabled}
            onChange={(event) => setSelectedScriptKey(event.target.value)}
            className={inputClass}
          >
            {scripts.map((script) => (
              <option key={script.product_script_key} value={script.product_script_key}>
                {script.kind === 'entry'
                  ? t('config.editAgentic.scripts.entryLabel')
                  : script.display_name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          data-testid="agentic-scripts-new"
          disabled={disabled}
          onClick={() => setNewScriptOpen(true)}
          className={textActionClass}
        >
          {t('config.editAgentic.scripts.newProductScript')}
        </button>
      </div>

      {newScriptOpen ? (
        <div className="flex flex-col gap-2 rounded-lg bg-surface-2 p-3 ring-1 ring-inset ring-ink-3/10">
          <label
            htmlFor="agentic-scripts-new-product-code"
            className="flex flex-col gap-1 text-meta text-ink-2"
          >
            {t('config.editAgentic.scripts.productCode')}
            {availableProducts.length === 0 ? (
              <p className="text-meta text-ink-3">
                {t('config.editAgentic.scripts.noAvailableProducts')}
              </p>
            ) : (
              <select
                id="agentic-scripts-new-product-code"
                data-testid="agentic-scripts-new-product-code"
                value={newProductCode}
                onChange={(event) => setNewProductCode(event.target.value)}
                className={inputClass}
              >
                <option value="">{t('config.editAgentic.scripts.selectProduct')}</option>
                {availableProducts.map((product) => (
                  <option key={product.product_code} value={product.product_code}>
                    {product.display_name}
                  </option>
                ))}
              </select>
            )}
          </label>
          <label
            htmlFor="agentic-scripts-new-display-name"
            className="flex flex-col gap-1 text-meta text-ink-2"
          >
            {t('config.editAgentic.scripts.displayName')}
            <input
              id="agentic-scripts-new-display-name"
              data-testid="agentic-scripts-new-display-name"
              type="text"
              value={newDisplayName}
              onChange={(event) => setNewDisplayName(event.target.value)}
              className={`${inputClass} w-full`}
            />
          </label>
          <div className="flex gap-2 self-end">
            <Button
              variant="primary"
              size="sm"
              data-testid="agentic-scripts-new-create"
              disabled={newProductCode === '' || newDisplayName.trim() === ''}
              onClick={createProductScript}
            >
              {t('config.editAgentic.scripts.create')}
            </Button>
            <Button
              size="sm"
              data-testid="agentic-scripts-new-cancel"
              onClick={() => setNewScriptOpen(false)}
            >
              {t('config.edit.dialogCancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {scripts.length <= 1 ? (
        <p data-testid="agentic-scripts-empty" className="text-body text-ink-2">
          {t('config.editAgentic.scripts.noProductScripts')}
        </p>
      ) : null}

      {selectedScript != null ? (
        <div className="flex flex-col gap-3">
          <h4 className="text-body font-medium text-ink-1">
            {t('config.editAgentic.scripts.stepsHeading', {
              name:
                selectedScript.kind === 'entry'
                  ? t('config.editAgentic.scripts.entryLabel')
                  : selectedScript.display_name,
            })}
          </h4>
          {errorFor(fieldErrors, `scripts[${selectedScript.product_script_key}].steps`) != null ? (
            <p
              role="alert"
              data-testid="agentic-scripts-all-disabled"
              className="text-meta text-status-down"
            >
              {t('config.editAgentic.scripts.allStepsDisabled')}
            </p>
          ) : null}

          {selectedScript.steps.length === 0 ? (
            <p data-testid="agentic-scripts-steps-empty" className="text-body text-ink-2">
              {t('config.editAgentic.scripts.noSteps')}
            </p>
          ) : (
            <ReorderableList
              items={selectedScript.steps}
              getId={(step) => step.step_id}
              onReorder={(next) => updateSteps(selectedScript.product_script_key, next)}
              ariaLabel={t('config.editAgentic.scripts.stepsHeading', {
                name: selectedScript.display_name,
              })}
              disabled={disabled}
              renderItem={(step) => (
                <div className="flex flex-1 flex-wrap items-center gap-2">
                  <label
                    htmlFor={`agentic-step-${step.step_id}-enabled`}
                    className="flex items-center gap-1.5"
                  >
                    <input
                      type="checkbox"
                      id={`agentic-step-${step.step_id}-enabled`}
                      data-testid={`agentic-step-${step.step_id}-enabled`}
                      aria-label={t('config.editAgentic.scripts.stepEnabled', {
                        stepId: step.step_id,
                      })}
                      checked={step.enabled}
                      disabled={disabled}
                      onChange={(event) =>
                        updateSteps(
                          selectedScript.product_script_key,
                          selectedScript.steps.map((s) =>
                            s.step_id === step.step_id
                              ? { ...s, enabled: event.target.checked }
                              : s,
                          ),
                        )
                      }
                    />
                  </label>
                  <button
                    type="button"
                    data-testid={`agentic-step-${step.step_id}-select`}
                    onClick={() => openStep(selectedScript.product_script_key, step.step_id)}
                    className="flex-1 text-left text-body text-ink-1"
                  >
                    {step.step_id}
                    {!step.enabled ? (
                      <span className="text-meta text-ink-3">
                        {' '}
                        {t('config.editAgentic.scripts.disabledSuffix')}
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    data-testid={`agentic-step-${step.step_id}-edit`}
                    onClick={() => openStep(selectedScript.product_script_key, step.step_id)}
                    className={textActionClass}
                  >
                    {t('config.editAgentic.scripts.editStep')}
                  </button>
                </div>
              )}
            />
          )}

          <button
            type="button"
            data-testid="agentic-scripts-add-step"
            disabled={disabled}
            onClick={() => addStep(selectedScript.product_script_key)}
            className={`self-start ${textActionClass}`}
          >
            {t('config.editAgentic.scripts.addStep')}
          </button>

          {(() => {
            const openStepId = openStepByScript[selectedScript.product_script_key];
            const step = openStepId
              ? selectedScript.steps.find((s) => s.step_id === openStepId)
              : null;
            if (step == null) {
              return (
                <p data-testid="agentic-scripts-no-step-selected" className="text-body text-ink-2">
                  {t('config.editAgentic.scripts.selectStep')}
                </p>
              );
            }
            return (
              <StepForm
                scriptKey={selectedScript.product_script_key}
                step={step}
                disabled={disabled}
                facts={facts}
                tools={tools}
                fieldErrors={fieldErrors}
                duplicateStepId={(allStepIds.get(step.step_id) ?? 0) > 1}
                onChange={(next) => {
                  updateSteps(
                    selectedScript.product_script_key,
                    selectedScript.steps.map((s) => (s.step_id === step.step_id ? next : s)),
                  );
                  // Renaming step_id would otherwise orphan the open-step
                  // pointer (keyed on the old id) and silently close the form.
                  if (next.step_id !== step.step_id)
                    openStep(selectedScript.product_script_key, next.step_id);
                }}
                onRemove={() => removeStep(selectedScript.product_script_key, step.step_id)}
                onClose={() => openStep(selectedScript.product_script_key, null)}
              />
            );
          })()}
        </div>
      ) : null}
    </section>
  );
};
